// /integrations — the connected-services page (PGS-6 / TRUST-7). A committee's first question is
// "what does it connect to"; today the honest answer was scattered across pricing and module pages.
//
// EVERY row below uses the approved wording of docs/marketing/CLAIMS_REGISTRY.md (Integrations
// table I1–I8 + C5 cost schedule + X1). Depth labels are truthful: "school-owned account",
// "configured per school", "reviewed during implementation" — never "seamless"/"one-click".
// The ⛔ boundaries (no Aadhaar eKYC, no live government sync) are stated explicitly on-page.
// Do not add a provider or capability here without a claims-registry row first.
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Cable, Check, FileSpreadsheet, IndianRupee, Landmark, Mail,
  MessageSquare, ScanLine, ShieldAlert, ShieldCheck,
} from "lucide-react";
import { ContactSalesModal, MarketingTopBar, SiteFooter } from "../components/marketing/shared";
import { MarketingMotion } from "../components/marketing/MarketingMotion";
import MarketingSeo from "../components/marketing/MarketingSeo";
import BrandWordmarkText from "../components/BrandWordmarkText";
import { Button } from "../components/ui/primitives";
import { init as initAnalytics, track } from "../lib/analytics";
import { CTA_PRIMARY_LABEL } from "../components/marketing/navigation";
import { Stagger, StaggerItem } from "./landingparts/parts1";

// Registry-approved integration rows. `depth` is the truthful availability label; `boundary`
// keeps the registry's ⚠️ conditions visible right next to the capability they qualify.
const INTEGRATIONS = [
  {
    id: "payments",
    icon: Landmark,
    accent: "text-primary bg-primary/10",
    title: "Payment gateways — your own account",
    body:
      "Collect through your own Razorpay or Cashfree account. Provider callbacks update the invoice "
      + "being paid; final settlement timing, deductions and bank credit remain provider and bank "
      + "evidence for the school to reconcile.",
    depth: "Live integration · school-owned account (BYOG)",
    boundary: "Vidyapeeth360 never pools or routes your fee money — collections settle to the school's own gateway account.",
  },
  {
    id: "whatsapp-sms",
    icon: MessageSquare,
    accent: "text-positive bg-positive-soft",
    title: "WhatsApp & SMS",
    body:
      "Configured WhatsApp and SMS workflows using school-approved templates — announcements, fee "
      + "reminders and configured parent communication.",
    depth: "Configured per school",
    boundary:
      "Provider onboarding, template and DLT approval, consent, acceptance, delivery and read "
      + "evidence remain channel-specific; the product itself is not “DLT-compliant”.",
  },
  {
    id: "email",
    icon: Mail,
    accent: "text-sky-dark bg-sky/10",
    title: "Transactional email",
    body: "Transactional email via SendGrid or your own SMTP, logged into the same delivery view as the other channels.",
    depth: "Configured per school",
  },
  {
    id: "udise-apaar",
    icon: FileSpreadsheet,
    accent: "text-teal-dark bg-teal/10",
    title: "UDISE+ & APAAR record preparation",
    body:
      "Prepare UDISE+ and APAAR records, review readiness findings, manage consent evidence and "
      + "import an APAAR ID after it is generated through the government process.",
    depth: "Record preparation & import — not a live government link",
    boundary: "Vidyapeeth360 does not generate APAAR IDs and has no live UDISE+ synchronization.",
  },
  {
    id: "gstin",
    icon: IndianRupee,
    accent: "text-orange-dark bg-orange-soft",
    title: "GSTIN verification",
    body: "GSTIN validation with optional live registry verification for the school's billing details.",
    depth: "Built in · optional live registry check",
  },
  {
    id: "tally",
    icon: FileSpreadsheet,
    accent: "text-ink-2 bg-muted",
    title: "Tally-oriented accounting exports",
    body:
      "Export accounting vouchers in a Tally-oriented XML format, alongside CSV exports on the "
      + "statements your accountant already works from.",
    depth: "Export path · reviewed with your accountant",
  },
  {
    id: "biometric",
    icon: ScanLine,
    accent: "text-ai-ink bg-ai/10",
    title: "Biometric attendance devices",
    body:
      "A supported biometric device can feed the attendance register so presence is recorded from "
      + "the device event rather than a manual roll call.",
    depth: "Device scope confirmed per deployment",
    boundary: "Device models, connectivity and server scope are confirmed for your deployment before rollout.",
  },
  {
    id: "digilocker",
    icon: ShieldCheck,
    accent: "text-primary bg-primary/10",
    title: "DigiLocker",
    body: "A deployment-specific DigiLocker integration can be reviewed during implementation.",
    depth: "Reviewed during implementation",
    boundary: "Not a general-availability live push; no government synchronization is promised.",
  },
];

// Registry ⛔ rows — the explicit not-offered list a committee needs in writing.
const NOT_OFFERED = [
  "Aadhaar e-KYC. Identity documents are uploaded manually and reviewed by the school administrator.",
  "Live government synchronization. There is no live UDISE+, DigiLocker or ABC data link; record preparation and post-generation imports are the shipped scope.",
  "Split settlement of school fees. Collections run on the school's own gateway account — never pooled through ours.",
];

export default function IntegrationsPage() {
  const navigate = useNavigate();
  const [salesOpen, setSalesOpen] = useState(false);

  useEffect(() => {
    initAnalytics();
    window.scrollTo(0, 0);
  }, []);

  return (
    <MarketingMotion>
    <div className="min-h-screen bg-canvas text-ink flex flex-col landing-page" data-testid="integrations-page">
      <MarketingSeo
        title="Integrations & Connected Services | Vidyapeeth360"
        description="What Vidyapeeth360 connects to — your own Razorpay or Cashfree account, WhatsApp, SMS and email channels, GSTIN checks and Tally-oriented exports."
        path="/integrations"
      />
      <MarketingTopBar onBookDemo={() => setSalesOpen(true)} />
      <main id="main" className="flex-1">
        {/* Header */}
        <section className="mx-auto max-w-6xl px-5 pt-12 sm:pt-16 pb-10">
          <div className="max-w-3xl">
            <p className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3.5 py-1.5 text-[13px] font-semibold text-ink-2">
              <Cable className="w-4 h-4 text-primary" aria-hidden="true" /> Integrations
            </p>
            <h1 className="mt-4 font-heading font-bold text-3xl sm:text-[40px] leading-[1.12] text-ink tracking-tight">
              What <BrandWordmarkText /> connects to — and what it deliberately doesn&rsquo;t.
            </h1>
            <p className="mt-4 text-ink-2 text-[16px] leading-relaxed max-w-2xl">
              Every integration below is described exactly as it ships: whose account it runs on,
              what your school configures, and where the boundary sits. Nothing on this page is a
              roadmap item, and the not-offered list is published on purpose.
            </p>
            <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink-2">
              {[
                "School-owned gateway accounts",
                "School-approved message templates",
                "Boundaries stated in writing",
              ].map((t) => (
                <li key={t} className="inline-flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-positive shrink-0" aria-hidden="true" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Integration rows */}
        <section className="border-y border-line bg-surface" aria-labelledby="integrations-list-h">
          <div className="mx-auto max-w-6xl px-5 py-12 sm:py-16">
            <h2 id="integrations-list-h" className="font-heading font-bold text-2xl sm:text-[28px] text-ink tracking-tight max-w-2xl">
              The connected services, one card each — including what each one does not do.
            </h2>
            <Stagger className="mt-8 grid gap-5 md:grid-cols-2" stagger={0.05} amount={0.1} data-testid="integrations-grid">
              {INTEGRATIONS.map((item) => (
                <StaggerItem as="article" key={item.id} className="rounded-2xl border border-line bg-canvas p-6" data-testid={`integration-${item.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <span className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${item.accent}`}>
                      <item.icon className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span className="rounded-full border border-line bg-surface px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-ink-subtle text-right">
                      {item.depth}
                    </span>
                  </div>
                  <h3 className="mt-4 font-heading font-semibold text-ink text-[16px] leading-snug">{item.title}</h3>
                  <p className="mt-2 text-[14px] text-ink-2 leading-relaxed">{item.body}</p>
                  {item.boundary && (
                    <p className="mt-3 flex gap-2 text-[13px] leading-relaxed text-ink-subtle">
                      <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-ink-subtle" aria-hidden="true" />
                      <span>{item.boundary}</span>
                    </p>
                  )}
                </StaggerItem>
              ))}
            </Stagger>
            <p className="mt-6 text-sm text-ink-2 max-w-3xl">
              One-time setup and recurring charges for integrations (WhatsApp setup, payment-gateway
              setup, biometric devices and server connectivity, Tally, GPS and more) are published in
              the{" "}
              <Link to="/pricing" className="font-medium text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary" data-testid="integrations-cost-link">
                cost schedule on the pricing page
              </Link>
              . The approved scope and tax treatment for your school remain written-order terms.
            </p>
          </div>
        </section>

        {/* Not offered — the ⛔ boundaries */}
        <section className="mx-auto max-w-6xl px-5 py-12 sm:py-16" aria-labelledby="integrations-not-h">
          <div className="max-w-3xl rounded-2xl border border-caution/30 bg-caution-soft/40 p-6 sm:p-7" data-testid="integrations-not-offered">
            <div className="flex items-start gap-3.5">
              <span className="mt-0.5 w-10 h-10 rounded-xl grid place-items-center shrink-0 text-caution bg-caution-soft">
                <ShieldAlert className="w-5 h-5" aria-hidden="true" />
              </span>
              <h2 id="integrations-not-h" className="font-heading font-semibold text-xl text-ink leading-snug">
                Not offered — stated plainly
              </h2>
            </div>
            <ul className="mt-4 space-y-3">
              {NOT_OFFERED.map((line) => (
                <li key={line.slice(0, 30)} className="flex gap-2.5 text-[14.5px] leading-relaxed text-ink-2">
                  <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-caution" aria-hidden="true" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[13px] leading-relaxed text-ink-subtle">
              If a vendor conversation depends on one of these, bring it to a walkthrough — we would
              rather lose an evaluation than mis-describe a boundary.
            </p>
          </div>
          <p className="mt-6 text-sm text-ink-2">
            Reviewing the trust posture behind these connections? See{" "}
            <Link to="/security#sub-processors" className="font-medium text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary">who processes your data (and how to get our DPA)</Link>
            {", "}the{" "}
            <Link to="/security" className="font-medium text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary">Security overview</Link>
            {" "}and the{" "}
            <Link to="/compliance/dpdp" className="font-medium text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary">DPDP guide for schools</Link>.
          </p>
        </section>

        {/* Close */}
        <section className="mx-auto max-w-6xl px-5 pb-14 sm:pb-16">
          <div className="rounded-3xl bg-gradient-to-br from-[var(--vp-close-band-from)] via-[var(--vp-close-band-via)] to-[var(--vp-close-band-to)] p-8 sm:p-12 text-center relative overflow-hidden">
            <h2 className="font-heading font-bold text-[26px] sm:text-[32px] text-white tracking-tight max-w-2xl mx-auto leading-tight">
              Bring your providers. We&rsquo;ll map the exact fit.
            </h2>
            <p className="text-white/75 mt-3 max-w-xl mx-auto leading-relaxed">
              Your gateway, your message channels, your accountant&rsquo;s workflow — a walkthrough
              confirms each connection against your school&rsquo;s real setup before you commit.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 shadow-lg" onClick={() => { track("trial_cta_click", { placement: "integrations_page" }); navigate("/register"); }} data-testid="integrations-register">
                {CTA_PRIMARY_LABEL} <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Button>
              <Button size="lg" variant="ghost" className="text-white border border-white/40 hover:bg-white/10" onClick={() => { track("demo_request", { placement: "integrations_page" }); setSalesOpen(true); }} data-testid="integrations-demo">
                Book a demo
              </Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <ContactSalesModal open={salesOpen} onClose={() => setSalesOpen(false)} contextLabel="integrations and connected services" placement="integrations_page" />
    </div>
    </MarketingMotion>
  );
}
